import { Components, Theme } from '@mui/material/styles';
import { TabsScrollButton } from './TabsScrollButton';
import { updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowTabs: Components<Theme>['MuiTabs'] = {
  defaultProps: {
    template: 'underlined',
    // for the A11y reflow-4x-zoom-scroll show scrollButtons instead of scrollbar
    scrollButtons: 'auto',
    allowScrollButtonsMobile: true,
    visibleScrollbar: false,
    variant: 'scrollable',
    slots: {
      scrollButtons: TabsScrollButton,
    },
  },
  styleOverrides: {
    root: ({ ownerState }) => ({
      minHeight: '0',
      alignItems: 'center',
      // space between tabs and scroll buttons (FlowScrollButton already contains 4px)
      gap: '0',
      ...(ownerState.fullWidth || ownerState.variant === 'fullWidth'
        ? {
            '& .MuiTab-root': {
              flexGrow: '1',
            },
          }
        : {}),
      // @mui omits centered property with variant scrollable,
      //  therefore code bellow has no effect. In docs, we recommend utilizing sx property.
      ...(ownerState.centered ? { justifyContent: 'center' } : {}),
      // check if we don't have any tab selected
      '&:not(:has(.MuiTab-root[aria-selected="true"]))': {
        // hide indicator
        '& .MuiTabs-indicator': {
          opacity: '0',
        },
      },
    }),
    flexContainer: ({ theme }) => ({
      gap: '16px',
      [theme.breakpoints.up('md')]: {
        gap: '28px',
      },
    }),
    indicator: ({ theme, ownerState }) => ({
      maxWidth: 'none',
      background: 'transparent',
      display: 'block',
      height: '3px',
      color: theme.palette.mode === 'dark' ? theme.palette.brand.secondary : theme.palette.brand.primary,
      ...(ownerState.indicatorColor === 'secondary' && {
        color: theme.palette.text.primary,
      }),
      // actual indicator line
      '&:before': {
        display: 'block',
        content: '""',
        position: 'absolute',
        left: '8px',
        right: '8px',
        top: '0',
        bottom: '0',
        backgroundColor: 'currentColor',
      },
    }),
    scroller: ({ ownerState }) => ({
      flexGrow: ownerState.fullWidth || ownerState.variant === 'fullWidth' ? '1' : '0',
      // restyle horizontal scrollbar
      '&::-webkit-scrollbar:horizontal': {
        height: '24px',
      },
      '&::-webkit-scrollbar-thumb:horizontal, &::-webkit-scrollbar-track:horizontal': {
        borderWidth: '10px',
      },
    }),
    scrollButtons: ({ theme }) => ({
      color: theme.palette.text.primary,
      // do not hide disabled scroll buttons
      '&.Mui-disabled': {
        opacity: '1',
        color: theme.palette.disabled.primary,
      },
    }),
  },
  variants: [
    {
      props: { template: 'underlined' },
      style: ({ theme }) => ({
        // align with rest of the page, due to extra space
        marginLeft: '-6px',
        marginRight: '-6px',
        maxWidth: 'none',
        // scroll container
        '& .MuiTabs-scroller': {
          '&::-webkit-scrollbar:horizontal': {
            height: '16px',
          },
          '&::-webkit-scrollbar-thumb:horizontal, &::-webkit-scrollbar-track:horizontal': {
            borderWidth: '6px',
          },
        },
        // container
        '& .MuiTabs-flexContainer': {
          // extra space for the focus indicator
          padding: '6px',
          // remove gap between tabs
          gap: '0',
        },
        // indicator line
        '& .MuiTabs-indicator': {
          // move directly into tab border
          bottom: '6px',
          '&:before': {
            // make full-width
            inset: '0',
          },
        },
        // Tabs itself
        '& .MuiTab-root': {
          // remove space around tab in favor of padding
          margin: '0',
          // increase spacing
          padding: '12px 24px',
          // responsive
          [theme.breakpoints.down('md')]: {
            paddingTop: '10px',
            paddingBottom: '10px',
          },
          // style adjust
          borderRadius: '0',
          color: theme.palette.mode === 'dark' ? theme.palette.onSurface.os300 : theme.palette.onSurface.os700,
          // font
          ...theme.typography.subheadline,
          [theme.breakpoints.up('xs')]: {
            fontWeight: '300',
          },
          // bottom border
          '&:after': {
            content: '""',
            position: 'absolute',
            left: '0',
            right: '0',
            bottom: '0',
            height: '1px',
            backgroundColor: theme.palette.onSurface.os500,
          },
          // hover effect
          '&:hover, &.Mui-focusVisible': {
            // increase size of the border
            '&:after': {
              height: '2px',
            },
          },
          // active
          '&.Mui-selected': {
            color: theme.palette.mode === 'dark' ? theme.palette.brand.secondary : theme.palette.brand.primary,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.surface.primary : theme.palette.transparent.primary,
          },
          // disabled
          '&.Mui-disabled': {
            color: theme.palette.disabled.tertiary,
          },
          // focused style
          '&.Mui-focusVisible': {
            // higher z-index than others
            zIndex: '10',
            // extra white border
            boxShadow: `0 0 0 2px ${theme.palette.background.primary}`,
            '&:before': {
              // move outside
              inset: '-2px',
              // add extra border radius
              borderRadius: '4px',
            },
          },
        },
      }),
    },
    {
      props: { template: 'buttons' },
      style: ({ theme }) => ({
        // container
        '& .MuiTabs-flexContainer': {
          // reduce gap between tabs
          gap: '8px',
        },
        // hide bottom line
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        // restyle tabs to buttons
        '& .MuiTab-root': {
          border: '1px solid transparent',
          padding: '6px 16px',
          borderRadius: '1000px',
          transition: 'all 0.3s ease-out',
          fontSize: '14px',
          fontWeight: '700',
          marginBottom: '2px',
          ...updateFocusRingStyle('0px', false),
          ...getInteractiveStyle(
            {
              backgroundColor: 'transparent',
              color: theme.palette.onSurface.os500,
            },
            {
              backgroundColor: theme.palette.surface.primary,
              color: theme.palette.text.primary,
            },
          ),
          '&.Mui-selected': {
            ...getInteractiveStyle(
              {
                backgroundColor: theme.palette.brand.quarternary,
                color: theme.palette.text.primary,
                borderColor: theme.palette.brand.primary,
              },
              {
                backgroundColor: theme.palette.brand.quarternary,
                color: theme.palette.brand.primary,
                borderColor: theme.palette.brand.primary,
              },
            ),
          },
          // responsive
          [theme.breakpoints.down('md')]: {
            '&, &.Mui-selected, & .FlowTab-selectedLabel': {
              fontWeight: '400',
            },
          },
        },
      }),
    },
    {
      // variant used in the SecondaryNavigation
      props: { template: 'navigation' },
      style: ({ theme }) => ({
        // container
        '& .MuiTabs-flexContainer': {
          // reduce gap between tabs
          gap: '4px',
        },
        // manual align
        paddingTop: '11px',
        [theme.breakpoints.down('xl')]: {
          paddingTop: '13px',
        },
        [theme.breakpoints.down('lg')]: {
          paddingTop: '8px',
        },
        // restyle tabs typography
        '& .MuiTab-root': {
          marginBottom: '11px',
          [theme.breakpoints.down('xl')]: {
            marginBottom: '13px',
          },
          [theme.breakpoints.down('lg')]: {
            marginBottom: '8px',
          },
          ...theme.typography.list,
        },
      }),
    },
    {
      props: { template: 'segmented' },
      style: ({ theme }) => ({
        // used for background
        color: theme.palette.mode === 'dark' ? theme.palette.surface.primary : theme.palette.transparent.secondary,
        // container
        '& .MuiTabs-scroller': {
          backgroundColor: 'currentColor',
          borderRadius: '1000px',
        },
        '& .MuiTabs-flexContainer': {
          // reduce gap between tabs
          gap: '8px',
        },
        // restyle indicator to white bordered travelling background
        '& .MuiTabs-indicator': {
          color: theme.palette.mode === 'dark' ? theme.palette.surface.secondary : theme.palette.common.white,
          // proportion
          height: '100%',
          // z-index bellow <Tab />
          zIndex: '10',
          borderRadius: '1000px',
          // make sure border from :before is not visible when no tab is selected
          overflow: 'hidden',
          '&:before': {
            inset: '0',
            borderRadius: 'inherit',
            border: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.brand.secondary : theme.palette.text.primary}`,
          },
        },
        // restyle tabs
        '& .MuiTab-root': {
          margin: '0',
          padding: '6px 16px',
          [theme.breakpoints.up('md')]: {
            padding: '8px 16px',
          },
          borderRadius: '1000px',
          transition: 'all 0.3s ease-out',
          // space between icon and text
          gap: '10px',
          // z-index above indicator
          zIndex: '20',
          // icon inside
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
            marginTop: '4px',
            marginBottom: '4px',
          },
          // change typography
          ...theme.typography.subheadline,
          color: theme.palette.text.primary,
          [theme.breakpoints.up('xs')]: {
            '&, &.Mui-selected, & .FlowTab-selectedLabel': {
              fontWeight: '400',
            },
          },
          // hover and focus
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: theme.palette.surface.primary,
          },
          // active
          '&.Mui-selected': {
            color: theme.palette.mode === 'dark' ? theme.palette.brand.secondary : theme.palette.text.primary,
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: 'transparent',
            },
          },
          // disabled
          '&.Mui-disabled': {
            color: theme.palette.disabled.tertiary,
          },
        },
      }),
    },
  ],
};
